import React, { useState } from "react";

import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";
import { formatDateTime } from "@/utils/date";

import { adsService } from "../services/adsService";
import { useInvalidateLiveCampaigns } from "../hooks/useLiveCampaigns";
import type { Campaign } from "../types";

interface CampaignDetailsModalProps {
  campaign: Campaign | null;
  isOpen: boolean;
  onClose: () => void;
}

type ActionKind = "pause" | "resume" | "cancel";

export const CampaignDetailsModal: React.FC<CampaignDetailsModalProps> = ({
  campaign,
  isOpen,
  onClose,
}) => {
  const invalidate = useInvalidateLiveCampaigns();
  const [pending, setPending] = useState<ActionKind | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!campaign) return null;

  const runAction = async (kind: ActionKind) => {
    setIsActing(true);
    setError(null);
    try {
      const fn =
        kind === "pause"
          ? adsService.pauseCampaign
          : kind === "resume"
            ? adsService.resumeCampaign
            : adsService.cancelCampaign;
      await fn(campaign.id);
      invalidate(campaign.clientId);
      setPending(null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setIsActing(false);
    }
  };

  const rec = campaign.agentRecommendation;
  const t = rec?.targeting;

  const remaining = campaign.budgetAllocated - campaign.budgetSpent;

  return (
    <>
      <Modal
        isOpen={isOpen && pending === null}
        onClose={onClose}
        title="Campaign details"
        size="lg"
        footer={
          <Footer
            status={campaign.status}
            isActing={isActing}
            onClose={onClose}
            onAction={setPending}
          />
        }
      >
        <div className="p-6 space-y-4 text-sm">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Kv label="Status" value={campaign.status} />
            <Kv label="Objective" value={campaign.campaignType} />
            <Kv
              label="Creative source"
              value={
                campaign.creativeSource === "standalone_ad"
                  ? "Standalone ad (uploaded creative)"
                  : "Existing post"
              }
            />
            <Kv
              label="Allocated"
              value={`€${campaign.budgetAllocated.toFixed(2)}`}
            />
            <Kv label="Spent" value={`€${campaign.budgetSpent.toFixed(2)}`} />
            <Kv label="Remaining" value={`€${remaining.toFixed(2)}`} />
            <Kv
              label="Approved by"
              value={campaign.approvedBy ?? "—"}
            />
            <Kv
              label="Approved at"
              value={formatDateTime(campaign.approvedAt)}
            />
            <Kv
              label="Created"
              value={formatDateTime(campaign.createdAt)}
            />
          </div>

          {campaign.creativeSource === "standalone_ad" && (
            <div className="rounded border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900">
              This ad runs as a standalone campaign. Engagement lives on the
              ad, not on the original post. Metrics appear here and in Meta
              Ads Manager; they will not surface on the Instagram Reel.
            </div>
          )}

          {rec?.reasoning && (
            <Section title="Agent reasoning">
              <p className="text-gray-700">{rec.reasoning}</p>
            </Section>
          )}

          {rec?.platforms && Object.keys(rec.platforms).length > 0 && (
            <Section title="Platforms">
              <div className="flex flex-col gap-2">
                {Object.entries(rec.platforms).map(([platform, block]) => {
                  if (!block) return null;
                  // Daily_budget + duration_days is the canonical shape.
                  // Legacy recommendations may only carry `budget` (total);
                  // derive daily from that so older campaigns render.
                  const duration = block.durationDays ?? null;
                  const daily =
                    block.dailyBudget ??
                    (block.budget != null && duration
                      ? block.budget / duration
                      : block.budget ?? 0);
                  const total =
                    block.budget ??
                    (block.dailyBudget != null && duration
                      ? block.dailyBudget * duration
                      : 0);
                  return (
                    <div
                      key={platform}
                      className="rounded border border-gray-200 bg-white px-3 py-2 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">
                          {platform}
                        </span>
                        <span className="text-gray-700">
                          €{daily.toFixed(2)}/day
                          {duration ? ` × ${duration}d` : ""} = €
                          {total.toFixed(2)} · {block.placements.join(", ")}
                        </span>
                      </div>
                      {block.reasoning && (
                        <p className="mt-1 text-gray-500">{block.reasoning}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {t && (
            <Section title={`Targeting · ${t.geographicScope}`}>
              <div className="space-y-1 text-gray-700">
                {t.locations?.map((loc) => (
                  <div key={loc.metaId}>
                    {loc.name}
                    {loc.radiusKm ? ` · ${loc.radiusKm} km radius` : ""}
                  </div>
                ))}
                {(t.ageMin != null || t.ageMax != null) && (
                  <div>
                    Age {t.ageMin ?? "?"}–{t.ageMax ?? "?"}
                  </div>
                )}
                {t.interests && t.interests.length > 0 && (
                  <div>
                    Interests: {t.interests.map((i) => i.name).join(", ")}
                  </div>
                )}
                {t.behaviors && t.behaviors.length > 0 && (
                  <div>
                    Behaviors: {t.behaviors.map((b) => b.name).join(", ")}
                  </div>
                )}
              </div>
            </Section>
          )}

          {rec?.estimatedReach != null && (
            <Kv
              label="Estimated reach"
              value={rec.estimatedReach.toLocaleString()}
            />
          )}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => {
          if (pending) runAction(pending);
        }}
        title={pending ? `${capitalize(pending)} campaign?` : ""}
        message={
          pending === "cancel"
            ? "Cancelling is permanent. The remaining budget will return to the monthly pool."
            : `Are you sure you want to ${pending ?? ""} this campaign?`
        }
        confirmText={pending ? capitalize(pending) : "Confirm"}
        variant={pending === "cancel" ? "danger" : "warning"}
        isLoading={isActing}
      />
    </>
  );
};

const Footer: React.FC<{
  status: Campaign["status"];
  isActing: boolean;
  onClose: () => void;
  onAction: (a: ActionKind) => void;
}> = ({ status, isActing, onClose, onAction }) => {
  const canPause = status === "ACTIVE";
  const canResume = status === "PAUSED";
  const canCancel = ["CREATING", "ACTIVE", "PAUSED"].includes(status);
  return (
    <div className="flex justify-between gap-2">
      <div className="flex gap-2">
        {canPause && (
          <Button variant="secondary" onClick={() => onAction("pause")} disabled={isActing}>
            Pause
          </Button>
        )}
        {canResume && (
          <Button variant="secondary" onClick={() => onAction("resume")} disabled={isActing}>
            Resume
          </Button>
        )}
        {canCancel && (
          <Button variant="destructive" onClick={() => onAction("cancel")} disabled={isActing}>
            Cancel campaign
          </Button>
        )}
      </div>
      <Button onClick={onClose} variant="ghost">
        Close
      </Button>
    </div>
  );
};

const Kv: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <div className="text-xs font-medium text-gray-500 uppercase">{label}</div>
    <div className="text-gray-900">{value}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="space-y-1">
    <div className="text-xs font-medium text-gray-500 uppercase">{title}</div>
    {children}
  </div>
);

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
